import { useState, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { addSubdomainToProject, publishPage } from '../api'
import { showSimpleError } from '@/lib/toast'

const MIN_ANIMATION_DURATION = 10000

export const usePublishFlow = (uniqueUrl, onClose, onPublishSuccess) => {
  const queryClient = useQueryClient()
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishState, setPublishState] = useState('initial') // 'initial' | 'publish-animation' | 'publishing' | 'success'
  const [publishParams, setPublishParams] = useState(null)
  const publishStartTimeRef = useRef(null)

  const handlePublishSuccess = useCallback(async () => {
    if (uniqueUrl) await queryClient.invalidateQueries({ queryKey: ['pageBlueprint', uniqueUrl] })

    const elapsed = Date.now() - (publishStartTimeRef.current || 0)
    const remainingTime = Math.max(0, MIN_ANIMATION_DURATION - elapsed)

    setTimeout(() => {
      setIsPublishing(false)
      setPublishState('success')
      // Call the success callback if provided
      onPublishSuccess?.()
    }, remainingTime)
  }, [uniqueUrl, queryClient, onPublishSuccess])

  const validateSubdomain = useCallback((customSubdomain, subdomainValue, projectSubdomain, subdomainValidation, isCheckingSubdomain) => {
    if (!customSubdomain) return null
    if (isCheckingSubdomain || subdomainValidation.status === 'checking') return 'Please wait for subdomain validation'
    if (!subdomainValue) return 'Please enter a subdomain'
    if (subdomainValidation.status !== 'success' && subdomainValue !== projectSubdomain) return 'Please fix subdomain validation errors'
    return null
  }, [])

  const executePublish = useCallback(async (params) => {
    if (!uniqueUrl) return showSimpleError('Project URL not found')

    setIsPublishing(true)
    try {
      const customSubdomain = params.subdomainEditor && params.subdomainValue !== params.projectSubdomain ? params.subdomainValue : null
      const validationError = validateSubdomain(customSubdomain, params.subdomainValue, params.projectSubdomain, params.subdomainValidation, params.isCheckingSubdomain)

      if (validationError) {
        showSimpleError(validationError)
        setIsPublishing(false)
        setPublishState('initial')
        return
      }

      const context = 'home'
      const publishOptions = { subdomain: true }

      if (params.isDomainConnected || params.isWordpressConnected) {
        await publishPage(context, uniqueUrl, { ...publishOptions, domain: params.isDomainConnected })
      } else if (customSubdomain) {
        await addSubdomainToProject(uniqueUrl, params.subdomainValue, { publishAfterAdd: true, activeStatus: { subdomain: true } })
      } else if (!params.isFirstTimePublish && (params.isSubdomainActive || params.projectSubdomain)) {
        await publishPage(context, uniqueUrl, { ...publishOptions, domain: false })
      } else {
        await addSubdomainToProject(uniqueUrl, params.projectSubdomain || uniqueUrl, { activeStatus: { subdomain: true } })
        try {
          await publishPage(context, uniqueUrl, { ...publishOptions, domain: false })
        } catch (err) {
          if (err?.message?.includes("Cannot read properties of null (reading '_id')")) {
            showSimpleError('Publish completed with a minor sync issue. If the site is not live, try again.')
          } else throw err
        }
      }
      await handlePublishSuccess()
    } catch (error) {
      showSimpleError(error.message || 'Failed to publish site')
      setPublishState('initial')
      setIsPublishing(false)
    }
  }, [uniqueUrl, handlePublishSuccess, validateSubdomain])

  const startPublishing = useCallback(() => {
    publishStartTimeRef.current = Date.now()
    setPublishState('publishing')
    if (publishParams) executePublish(publishParams)
  }, [publishParams, executePublish])

  const publish = useCallback((params) => {
    setPublishParams(params)
    setPublishState('publish-animation')
  }, [])

  const handleSuccessDone = useCallback(() => {
    setPublishState('initial')
    setIsPublishing(false)
    onClose?.()
  }, [onClose])

  return { isPublishing, publishState, publish, startPublishing, handleSuccessDone }
}

